<template>
  <div class="collection-member-list">
    <div class="clearfix members-header">
      <h4>{{ _('Members') }}</h4>
      <button
        v-if="canManage"
        type="button"
        class="btn btn-success pull-right"
        v-on:click="inviteMember"
        :disabled="!canInvite"
      >
        <i class="fa fa-fw fa-user"></i> {{ _('Add a user...') }}
      </button>
    </div>
    <p v-if="emptyHint" class="help-block">{{ emptyHint }}</p>
    <div v-if="hasAllUsersAccess" class="alert alert-info">
      <i class="fa fa-fw fa-users"></i>
      {{ _('Everyone has access to this collection because the All users group is selected.') }}
      <template v-if="hasMemberScope">
        {{ _('Users listed below have separate access and will keep it if the All users group is removed.') }}
      </template>
    </div>
    <template v-if="canManage">
      <div class="list-filter-row" v-if="hasMemberScope">
        <div class="list-filter-col filtercol">
          <div class="form-group">
            <div class="input-group">
              <input class="form-control" type="text" v-model="search" :placeholder="_('Keyword search')" />
            </div>
          </div>
        </div>
        <div class="list-filter-col filtercol" v-if="showGroupFilter">
          <div class="form-group">
            <select class="form-control" v-model="groupFilter">
              <option value="all">{{ _('All user groups') }}</option>
              <option v-for="group in editableGroups" :key="group.id" :value="parseInt(group.id)">{{ group.name }}</option>
            </select>
          </div>
        </div>
      </div>
      <div v-if="pendingInvites && pendingInvites.length" style="margin-bottom: 8px;">
        <h5>{{ _('Pending invitations') }}</h5>
        <table class="table table-condensed" style="margin-bottom: 0;">
          <thead>
            <tr>
              <th>{{ _('Name') }}</th>
              <th>{{ _('Email') }}</th>
              <th style="width: 120px;">{{ _('Start date') }}</th>
              <th style="width: 120px;">{{ _('End date') }}</th>
              <th style="width: 200px;">{{ _('Status') }}</th>
              <th style="width: 36px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="invite in pendingInvites" :key="invite.tempId" class="text-muted">
              <td><strong>{{ invite.name }}</strong></td>
              <td><small>{{ invite.email }}</small></td>
              <td><small>{{ invite.startdate || '' }}</small></td>
              <td><small>{{ invite.enddate || '' }}</small></td>
              <td><span class="label label-default">{{ _('Will be sent when you save') }}</span></td>
              <td><a href="javascript:;" v-on:click="$emit('remove-pending', invite)"><i class="fa fa-fw fa-times text-danger"></i></a></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="editingMember && editingField"
        class="member-date-overlay"
        :style="overlayStyle"
      >
        <DatePicker
          ref="inlinePicker"
          v-model="editingDate"
          :min-date="inlineMinDate"
          :max-date="inlineMaxDate"
          :shortLocale="shortLocale"
          :placeholder="editingField === 'enddate' ? _('No end date') : _('No start date')"
          :staticInput="true"
          :clearable="true"
          :field="editingField === 'enddate' ? 'memberend' : 'memberstart'"
          v-on:update:modelValue="onInlineDate"
          v-on:closed="onPickerClosed"
        />
      </div>
      <Datatable
        v-if="hasMemberScope"
        :key="'members-' + String(effectiveGroupFilter || 'none')"
        :url="usersUrl"
        :search="search"
        :group-filter="effectiveGroupFilter"
        :options="tableOptions"
        ref="datatable"
      />
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import { DateTime } from 'luxon'
import components from '~/lib/components'
import InviteUserModal from '~/components/modals/InviteUserModal'
import { DatePicker } from '#components'

export default {
  mixins: [components],
  components: { DatePicker },
  props: {
    groupId: { default: null },
    collectionId: { default: null },
    groups: { type: Array, default: () => [] },
    showGroupColumn: { type: Boolean, default: false },
    contextLabel: { type: String, default: '' },
    pendingInvites: { type: Array, default: () => [] },
    emptyHint: { type: String, default: '' },
    readOnly: { type: Boolean, default: false },
    detailsTrail: { type: Array, default: null },
    hasAllUsersAccess: { type: Boolean, default: false }
  },
  emits: ['queued', 'remove-pending', 'observe', 'unobserve'],
  data () {
    const _ = useNuxtApp().$translate
    const $this = this
    return {
      search: '',
      groupFilter: 'all',
      editingMember: null,
      editingField: null,
      editingDate: null,
      ignoreDateUpdate: false,
      overlayStyle: {},
      savingDates: false,
      tableOptions: {
        persistence: false,
        persistFilters: false,
        persistenceSuffix: 'members',
        index: 'rowid',
        columns: [
          { title: _('Name'), field: 'name', widthGrow: 2, responsive: 0 },
          { title: _('Email'), field: 'email', widthGrow: 2, responsive: 3 },
          {
            title: _('User group'),
            field: 'groupname',
            widthGrow: 2,
            visible: !!this.showGroupColumn,
            responsive: 2
          },
          {
            title: _('Start date'),
            field: 'startdate',
            width: 140,
            responsive: 0,
            cssClass: 'member-date-cell',
            formatter (cell) {
              return $this.formatMembershipDateCell(cell.getValue())
            },
            cellClick (e, cell) {
              if (e && e.stopPropagation) e.stopPropagation()
              $this.editDateCell(cell.getRow().getData(), 'startdate', cell)
            }
          },
          {
            title: _('End date'),
            field: 'enddate',
            width: 140,
            responsive: 0,
            cssClass: 'member-date-cell',
            formatter (cell) {
              return $this.formatMembershipDateCell(cell.getValue())
            },
            cellClick (e, cell) {
              if (e && e.stopPropagation) e.stopPropagation()
              $this.editDateCell(cell.getRow().getData(), 'enddate', cell)
            }
          },
          {
            title: '',
            field: 'actions',
            width: 50,
            hozAlign: 'center',
            headerSort: false,
            responsive: 0,
            formatter () {
              return '<i class="fa fa-fw fa-trash text-danger" style="cursor:pointer"></i>'
            },
            cellClick (e, cell) {
              if (e && e.stopPropagation) e.stopPropagation()
              $this.removeMember(cell.getRow().getData())
            }
          }
        ],
        initialSort: [{ column: 'name', dir: 'asc' }],
        rowClick (e, row) {
          if ($this.isRowActionClick(e)) return
          $this.openUser(row.getData())
        }
      }
    }
  },
  computed: {
    usersUrl () {
      return this.baseDomain + this.shopId + '/users.json?apikey=' + this.apiKey
    },
    editableGroups () {
      return (this.groups || []).filter(group => group && !parseInt(group.allusers))
    },
    showGroupFilter () {
      return this.showGroupColumn && this.editableGroups.length > 1
    },
    effectiveGroupFilter () {
      if (this.groupFilter && this.groupFilter !== 'all') {
        return this.groupFilter
      }
      if (this.editableGroups.length) {
        return this.editableGroups.map(group => parseInt(group.id)).filter(id => id > 0).join(',')
      }
      if (this.groupId) {
        return this.groupId
      }
      return null
    },
    targetGroupId () {
      if (this.groupFilter && this.groupFilter !== 'all') return parseInt(this.groupFilter)
      if (this.groupId) return parseInt(this.groupId)
      if (this.editableGroups.length === 1) return parseInt(this.editableGroups[0].id)
      return null
    },
    hasMemberScope () {
      return !!this.effectiveGroupFilter
    },
    canManage () {
      return !this.readOnly
    },
    canInvite () {
      return this.canManage && (this.hasMemberScope || !this.collectionId)
    },
    showInviteGroupSelector () {
      return this.showGroupColumn && this.editableGroups.length > 1 && !this.targetGroupId
    },
    memberExcludeGroupIds () {
      if (this.collectionId && this.editableGroups.length) {
        return this.editableGroups.map(group => parseInt(group.id)).filter(id => id > 0)
      }
      if (this.targetGroupId) {
        return [parseInt(this.targetGroupId)]
      }
      return []
    },
    memberDetailsTrail () {
      if (this.detailsTrail && this.detailsTrail.length) {
        return this.detailsTrail
      }
      return this.componentTrail
    },
    inlineMinDate () {
      if (this.editingField !== 'enddate' || !this.editingMember) return null
      return this.isoToPickerDate(this.editingMember.startdate)
    },
    inlineMaxDate () {
      if (this.editingField !== 'startdate' || !this.editingMember) return null
      return this.isoToPickerDate(this.editingMember.enddate)
    }
  },
  watch: {
    showGroupColumn () {
      this.$nextTick(() => this.syncGroupColumn())
    },
    effectiveGroupFilter () {
      this.closeDateEditor()
    }
  },
  methods: {
    tableColumn (field) {
      const tabulator = this.$refs?.datatable?.tabulator
      if (!tabulator || tabulator.destroyed) {
        return null
      }
      try {
        const columns = tabulator.getColumns()
        if (!columns.some(col => col.getField() === field)) {
          return null
        }
        return tabulator.getColumn(field)
      } catch (e) {
        return null
      }
    },
    syncGroupColumn () {
      const column = this.tableColumn('groupname')
      if (column) {
        if (this.showGroupColumn) {
          column.show()
        } else {
          column.hide()
        }
      } else if (this.tableOptions.columns) {
        const def = this.tableOptions.columns.find(col => col.field === 'groupname')
        if (def) def.visible = !!this.showGroupColumn
      }
    },
    formatMembershipDate (value) {
      if (!value) return ''
      const iso = String(value).slice(0, 10)
      const parsed = DateTime.fromISO(iso)
      return parsed.isValid ? parsed.toFormat(this.dateFormat) : iso
    },
    formatMembershipDateCell (value) {
      const formatted = this.formatMembershipDate(value)
      if (formatted) {
        return '<span class="member-date-value">' + formatted + '</span>'
      }
      return '<span class="text-muted member-date-empty"><i class="fa fa-fw fa-calendar"></i></span>'
    },
    toMembershipDate (value) {
      if (!value) return null
      if (value instanceof Date) {
        return DateTime.fromJSDate(value).toISODate() || null
      }
      if (typeof value === 'string' && value.trim()) {
        return value.trim().slice(0, 10)
      }
      return null
    },
    reload () {
      if (this.$refs.datatable) {
        this.$refs.datatable.reload()
      }
    },
    isRowActionClick (e) {
      const target = e && e.target
      if (!target || !target.closest) return false
      if (target.closest('.fa-trash') || target.closest('.member-date-cell') || target.closest('.member-date-empty') || target.closest('.member-date-value') || target.closest('.member-date-overlay')) return true
      const field = target.closest('.tabulator-cell')?.getAttribute('tabulator-field')
      return field === 'actions' || field === 'startdate' || field === 'enddate'
    },
    openUser (user) {
      if (!user || !user.id) return
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'CollectionUserDetails',
        title: user.name || this._('User'),
        width: 6,
        data: {
          userId: user.id,
          trail: this.memberDetailsTrail
        }
      })
    },
    isoToPickerDate (value) {
      const iso = this.toMembershipDate(value)
      if (!iso) return null
      const parsed = DateTime.fromISO(iso)
      return parsed.isValid ? parsed.toJSDate() : null
    },
    closeDateEditor () {
      this.editingMember = null
      this.editingField = null
      this.editingDate = null
      this.ignoreDateUpdate = false
      this.overlayStyle = {}
    },
    onPickerClosed () {
      if (this.savingDates) return
      this.closeDateEditor()
    },
    editDateCell (member, field, cell) {
      if (!member || !field) return
      const el = cell && cell.getElement ? cell.getElement() : null
      const rect = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null
      this.ignoreDateUpdate = true
      this.editingMember = member
      this.editingField = field
      this.editingDate = this.isoToPickerDate(member[field])
      this.overlayStyle = rect
        ? {
            top: rect.top + 'px',
            left: rect.left + 'px',
            width: Math.max(rect.width, 160) + 'px',
            height: rect.height + 'px'
          }
        : {}
      this.$nextTick(() => {
        if (this.$refs.inlinePicker && this.$refs.inlinePicker.open) {
          this.$refs.inlinePicker.open()
        }
        this.ignoreDateUpdate = false
      })
    },
    async onInlineDate (value) {
      if (this.ignoreDateUpdate || this.savingDates || !this.editingMember) return
      const next = this.toMembershipDate(value)
      const current = this.toMembershipDate(this.editingMember[this.editingField])
      if (next === current) return
      await this.saveInlineDate(value)
    },
    updateMemberRow (member, patch) {
      Object.assign(member, patch)
      const tabulator = this.$refs?.datatable?.tabulator
      if (!tabulator || tabulator.destroyed) return
      try {
        const row = tabulator.getRow(member.rowid)
        if (row) row.update(patch)
      } catch (e) {}
    },
    async saveInlineDate (value) {
      const member = this.editingMember
      const field = this.editingField
      if (!member || !field) return
      const groupId = parseInt(member.groupid || this.targetGroupId)
      if (!groupId) return
      const next = this.toMembershipDate(value)
      const current = this.toMembershipDate(member[field])
      if (next === current) {
        this.closeDateEditor()
        return
      }
      const startdate = field === 'startdate' ? next : this.toMembershipDate(member.startdate)
      const enddate = field === 'enddate' ? next : this.toMembershipDate(member.enddate)
      if (startdate && enddate && enddate < startdate) {
        return this.showError(this._('The end date cannot be before the start date.'))
      }
      this.savingDates = true
      try {
        await axios.patch(
          this.baseDomain + this.shopId + '/usergroups/' + groupId + '.json?apikey=' + this.apiKey,
          {
            id: groupId,
            updateMembers: [{ id: parseInt(member.id), startdate, enddate }]
          }
        )
        this.updateMemberRow(member, { startdate, enddate })
        this.EventBus.$emit('collectionUsersChanged')
        this.closeDateEditor()
      } catch (e) {
        this.showError(e)
      }
      this.savingDates = false
    },
    async removeMember (member) {
      const groupId = parseInt(member.groupid || this.targetGroupId)
      if (!groupId || !member.id) return
      if (!confirm(this._('Remove this user from the user group?'))) return
      try {
        await axios.patch(
          this.baseDomain + this.shopId + '/usergroups/' + groupId + '.json?apikey=' + this.apiKey,
          { id: groupId, removeMembers: [parseInt(member.id)] }
        )
        if (this.editingMember && parseInt(this.editingMember.id) === parseInt(member.id)) {
          this.closeDateEditor()
        }
        this.reload()
        this.EventBus.$emit('collectionUsersChanged')
      } catch (e) {
        this.showError(e)
      }
    },
    inviteMember () {
      const groupId = this.targetGroupId
      this.showModal('invite-member', InviteUserModal, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          locale: this.locale,
          advancedCollections: this.$store.getters.advancedCollections ? 1 : 0,
          groupsUrl: this.baseDomain + this.shopId + '/usergroups.json?apikey=' + this.apiKey,
          availableGroups: this.editableGroups,
          presetGroupIds: groupId ? [groupId] : [],
          queueOnly: !groupId && !this.showInviteGroupSelector,
          contextLabel: this.contextLabel,
          hideGroupSelector: !this.showInviteGroupSelector,
          allowExisting: !!(this.groupId || this.collectionId),
          usersUrl: this.usersUrl,
          excludeGroupIds: this.memberExcludeGroupIds
        },
        width: 560,
        backgroundEvents: {
          queued: (invite) => this.$emit('queued', invite),
          added: () => {
            this.reload()
            this.EventBus.$emit('collectionUsersChanged')
          }
        },
        handlers: {
          closed: () => this.reload()
        }
      })
    }
  },
  mounted () {
    this.syncGroupColumn()
  }
}
</script>

<style scoped>
.members-header {
  margin-bottom: 12px;
}
.members-header h4 {
  display: inline-block;
  margin-top: 0;
  margin-bottom: 0;
  line-height: 34px;
}
.collection-member-list :deep(.member-date-cell) {
  cursor: pointer;
}
.collection-member-list :deep(.member-date-empty) {
  opacity: 0.55;
}
.member-date-overlay {
  position: fixed;
  z-index: 1060;
}
.member-date-overlay :deep(.vuedatepicker) {
  width: 100%;
  height: 100%;
}
.member-date-overlay :deep(.form-control) {
  height: 100%;
  padding-top: 2px;
  padding-bottom: 2px;
}
</style>
